import { create, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Storage } from '@/utils/storage';
import useSiderStore from '@/stores/sider-menu';

/** agent 侧边栏打开状态的 localStorage key */
const AGENT_SIDEBAR_OPEN_KEY = 'agent_sidebar_open';

/**
 * SupplierAgentGlobal 全局状态管理 store 接口定义
 */
interface SupplierAgentGlobalStoreState {
  /** agent 侧边栏是否打开 */
  agentSidebarOpen: boolean;
  /** 设置 agent 侧边栏打开/关闭状态，并同步到 localStorage */
  setAgentSidebarOpen: (open: boolean) => void;
  /** 切换 agent 侧边栏打开/关闭状态，并同步到 localStorage */
  toggleAgentSidebar: () => void;
}

/**
 * 创建全局唯一的 SupplierAgentGlobal store 实例
 * 整个应用共享这一个 store 实例
 */
export const supplierAgentGlobalStore = create<SupplierAgentGlobalStoreState>()(
  immer(set => {
    return {
      // 从 localStorage 读取初始值，默认为 true（打开）
      agentSidebarOpen: Storage.getItem<boolean>(AGENT_SIDEBAR_OPEN_KEY) ?? true,
      setAgentSidebarOpen: (open: boolean) => {
        // 同步更新 localStorage
        Storage.setItem(AGENT_SIDEBAR_OPEN_KEY, open);
        set({ agentSidebarOpen: open });
        // 互斥：打开 AI Agent 侧边栏时，收起左侧菜单栏
        if (open) {
          useSiderStore.getState().setCollapsed(true);
        }
      },
      toggleAgentSidebar: () => {
        // 统一走 setAgentSidebarOpen，确保互斥逻辑生效
        const next = !supplierAgentGlobalStore.getState().agentSidebarOpen;
        supplierAgentGlobalStore.getState().setAgentSidebarOpen(next);
      },
    };
  })
);

/**
 * SupplierAgentGlobal 全局 store 选择器 hook
 * 用于在组件中选择和使用全局 store 的状态
 * @param selector - 状态选择器函数，用于选择需要的部分状态
 * @returns 选择的状态
 */
export const useSupplierAgentGlobalStore = <T>(selector: (state: SupplierAgentGlobalStoreState) => T): T => {
  return useStore(supplierAgentGlobalStore, selector);
};

/**
 * 获取整个 SupplierAgentGlobal store 实例
 * 用于在需要直接操作 store 的场景
 * @returns store 实例
 */
export const getSupplierAgentGlobalStore = () => supplierAgentGlobalStore;

/**
 * 直接获取当前 SupplierAgentGlobal 状态
 * 用于在非组件环境中获取当前状态
 * @returns 当前状态
 */
export const getSupplierAgentGlobalState = () => supplierAgentGlobalStore.getState();
